import React, { useState, useEffect } from 'react';
import { sendSuggestionFeedback } from '../../services/api';
import { 
  getSortingTemplates, 
  createSortingTemplate, 
  updateSortingTemplate, 
  deleteSortingTemplate, 
  applySortingTemplate,
  getUserTags,
  getAITemplateSuggestion 
} from '../../services/api';

const TemplateManager = ({ className = "", onTemplatesChange = null }) => {
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [applying, setApplying] = useState(null);
  const [pathStyleHints, setPathStyleHints] = useState({ nestingDepth: 0, usesYearSegment: false, usesCategorySegment: false, variablesCount: 0 });
  const [pathSuggestions, setPathSuggestions] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    criteria: {
      file_type: [],
      name_pattern: '',
      size_min: '',
      size_max: '',
      tags: []
    },
    actions: {
      add_tags: [],
      set_category: '',
      folder_structure: ''
    },
    is_active: true,
    auto_apply: false
  });

  const fileTypeOptions = ['pdf', 'docx', 'doc', 'png', 'jpg', 'jpeg', 'csv', 'xlsx', 'xls'];

  useEffect(() => {
    loadTemplates();
    loadTags();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const userTemplates = await getSortingTemplates();
      setTemplates(userTemplates);
      if (onTemplatesChange) onTemplatesChange(userTemplates);
      // refresh suggestions based on existing templates
      setPathSuggestions(generateTemplatePathSuggestions(userTemplates));
    } catch (err) {
      setError(`Failed to load templates: ${err.message}`);
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const userTags = await getUserTags();
      setTags(userTags);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      criteria: {
        file_type: [],
        name_pattern: '',
        size_min: '',
        size_max: '',
        tags: []
      },
      actions: {
        add_tags: [],
        set_category: '',
        folder_structure: ''
      },
      is_active: true,
      auto_apply: false
    });
    setPathStyleHints({ nestingDepth: 0, usesYearSegment: false, usesCategorySegment: false, variablesCount: 0 });
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      // Clean up form data
      const cleanData = {
        ...formData,
        criteria: {
          ...formData.criteria,
          file_type: formData.criteria.file_type.length > 0 ? formData.criteria.file_type : null,
          name_pattern: formData.criteria.name_pattern || null,
          size_min: formData.criteria.size_min ? parseInt(formData.criteria.size_min) : null,
          size_max: formData.criteria.size_max ? parseInt(formData.criteria.size_max) : null,
          tags: formData.criteria.tags.length > 0 ? formData.criteria.tags : null
        },
        actions: {
          ...formData.actions,
          add_tags: formData.actions.add_tags.length > 0 ? formData.actions.add_tags : null,
          set_category: formData.actions.set_category || null
        }
      };

      await createSortingTemplate(cleanData);
      setShowCreateModal(false);
      resetForm();
      await loadTemplates();
    } catch (err) {
      alert(`Failed to create template: ${err.message}`);
    }
  };

  const handleEditTemplate = async (e) => {
    e.preventDefault();
    try {
      // Clean up form data
      const cleanData = {
        ...formData,
        criteria: {
          ...formData.criteria,
          file_type: formData.criteria.file_type.length > 0 ? formData.criteria.file_type : null,
          name_pattern: formData.criteria.name_pattern || null,
          size_min: formData.criteria.size_min ? parseInt(formData.criteria.size_min) : null,
          size_max: formData.criteria.size_max ? parseInt(formData.criteria.size_max) : null,
          tags: formData.criteria.tags.length > 0 ? formData.criteria.tags : null
        },
        actions: {
          ...formData.actions,
          add_tags: formData.actions.add_tags.length > 0 ? formData.actions.add_tags : null,
          set_category: formData.actions.set_category || null
        }
      };

      await updateSortingTemplate(editingTemplate.template_id, cleanData);
      setShowEditModal(false);
      setEditingTemplate(null);
      resetForm();
      await loadTemplates();
    } catch (err) {
      alert(`Failed to update template: ${err.message}`);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (window.confirm(`Are you sure you want to delete the template "${template.name}"?`)) {
      try {
        await deleteSortingTemplate(template.template_id);
        await loadTemplates();
      } catch (err) {
        alert(`Failed to delete template: ${err.message}`);
      }
    }
  };

  const handleApplyTemplate = async (template) => {
    try {
      setApplying(template.template_id);
      const result = await applySortingTemplate(template.template_id);
      alert(`Template applied successfully! ${result.applied_count} files were processed.`);
      await loadTemplates(); // Refresh to update usage counts
    } catch (err) {
      alert(`Failed to apply template: ${err.message}`);
    } finally {
      setApplying(null);
    }
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      criteria: {
        file_type: template.criteria.file_type || [],
        name_pattern: template.criteria.name_pattern || '',
        size_min: template.criteria.size_min || '',
        size_max: template.criteria.size_max || '',
        tags: template.criteria.tags || []
      },
      actions: {
        add_tags: template.actions.add_tags || [],
        set_category: template.actions.set_category || '',
        folder_structure: template.actions.folder_structure || ''
      },
      is_active: template.is_active,
      auto_apply: template.auto_apply
    });
    // derive style hints for edit modal
    updatePathStyleHints(template.actions.folder_structure || '');
    setShowEditModal(true);
  };

  // Utilities for folder structure field
  const parseTemplateVariables = (path) => {
    const regex = /<([^>]+)>/g;
    const variables = [];
    let m;
    while ((m = regex.exec(path)) !== null) {
      const name = m[1];
      if (!variables.includes(name)) variables.push(name);
    }
    return variables;
  };

  const updatePathStyleHints = (path) => {
    const vars = parseTemplateVariables(path);
    const segs = (path || '').split('/').filter(Boolean);
    setPathStyleHints({
      nestingDepth: segs.length,
      usesYearSegment: segs.some(s => /\b20\d{2}\b|\[year\]|<year>/i.test(s)),
      usesCategorySegment: segs.some(s => /(receipts|invoices|contracts|photos|videos|audio|documents|\[category\]|<category>)/i.test(s)),
      variablesCount: vars.length
    });
  };

  const generateTemplatePathSuggestions = (existingTemplates = []) => {
    const set = new Set();
    // from existing templates
    existingTemplates.forEach(t => {
      const fs = t.actions?.folder_structure;
      if (fs) set.add(fs.endsWith('/') ? fs : `${fs}/`);
    });
    // common patterns
    set.add('Documents/<Year>/<Category>/');
    set.add('Documents/<Year>/receipts/');
    set.add('Documents/<Year>/invoices/');
    set.add('Projects/<Project>/<Year>/');
    set.add('Clients/<Client>/<Year>/');
    set.add('Photos/<Person>/<Age>/');
    set.add('Photos/<Year>/<Event>/');
    set.add('Media/Photos/<Year>/<Month>/');
    return Array.from(set).slice(0, 8);
  };

  const handleAISuggestion = async () => {
    try {
      setAiSuggesting(true);
      const suggestion = await getAITemplateSuggestion('I want to better organize my files');
      
      if (suggestion.template_suggestion && suggestion.template_suggestion.name) {
        const aiTemplate = suggestion.template_suggestion;
        setFormData({
          name: aiTemplate.name || 'AI Suggested Template',
          description: aiTemplate.description || '',
          criteria: {
            file_type: aiTemplate.criteria?.file_type || [],
            name_pattern: aiTemplate.criteria?.name_pattern || '',
            size_min: aiTemplate.criteria?.size_min || '',
            size_max: aiTemplate.criteria?.size_max || '',
            tags: aiTemplate.criteria?.tags || []
          },
          actions: {
            add_tags: aiTemplate.actions?.add_tags || [],
            set_category: aiTemplate.actions?.set_category || ''
          },
          is_active: true,
          auto_apply: aiTemplate.auto_apply || false
        });
        setShowCreateModal(true);
      } else {
        alert('AI suggestion received! Check the response for details.');
      }
    } catch (err) {
      alert(`AI suggestion failed: ${err.message}`);
    } finally {
      setAiSuggesting(false);
    }
  };

  const toggleFileType = (type) => {
    const newTypes = formData.criteria.file_type.includes(type)
      ? formData.criteria.file_type.filter(t => t !== type)
      : [...formData.criteria.file_type, type];
    setFormData({
      ...formData,
      criteria: { ...formData.criteria, file_type: newTypes }
    });
  };

  const toggleTag = (tagId, isAction = false) => {
    const field = isAction ? 'actions' : 'criteria';
    const key = isAction ? 'add_tags' : 'tags';
    const current = formData[field][key];
    const newTags = current.includes(tagId)
      ? current.filter(t => t !== tagId)
      : [...current, tagId];
    setFormData({
      ...formData,
      [field]: { ...formData[field], [key]: newTags }
    });
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-400"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading templates...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <i className="fas fa-exclamation-triangle text-red-400"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading templates</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <button 
                onClick={loadTemplates}
                className="mt-2 text-sm text-red-800 underline hover:text-red-900"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Sorting Templates ({templates.length})
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Create automated rules to organize your files
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleAISuggestion}
            disabled={aiSuggesting}
            className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {aiSuggesting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                AI Suggesting...
              </>
            ) : (
              <>
                <i className="fas fa-magic mr-2"></i>
                AI Suggest
              </>
            )}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
          >
            <i className="fas fa-plus mr-2"></i>
            Create Template
          </button>
        </div>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <div className="text-center py-12">
          <i className="fas fa-cogs text-4xl text-gray-400 dark:text-gray-500 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No templates yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Create your first sorting template to automate file organization
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
          >
            <i className="fas fa-plus mr-2"></i>
            Create Your First Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {templates.map((template) => (
            <div
              key={template.template_id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{template.name}</h4>
                    <div className="flex space-x-2">
                      {template.is_active && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                          Active
                        </span>
                      )}
                      {template.auto_apply && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                          Auto-apply
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {template.description || 'No description'}
                  </p>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Used {template.usage_count} times • Created {new Date(template.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex space-x-1 ml-4">
                  <button
                    onClick={() => handleApplyTemplate(template)}
                    disabled={applying === template.template_id}
                    className="p-2 text-green-600 hover:text-green-800 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Apply template"
                  >
                    {applying === template.template_id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                    ) : (
                      <i className="fas fa-play text-sm"></i>
                    )}
                  </button>
                  <button
                    onClick={() => openEditModal(template)}
                    className="p-2 text-blue-600 hover:text-blue-800 transition duration-200"
                    title="Edit template"
                  >
                    <i className="fas fa-edit text-sm"></i>
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template)}
                    className="p-2 text-red-600 hover:text-red-800 transition duration-200"
                    title="Delete template"
                  >
                    <i className="fas fa-trash text-sm"></i>
                  </button>
                </div>
              </div>
              
              {/* Criteria */}
              <div className="mb-4">
                <h6 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">CRITERIA</h6>
                <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  {template.criteria.file_type && (
                    <div>File types: {template.criteria.file_type.join(', ')}</div>
                  )}
                  {template.criteria.name_pattern && (
                    <div>Name pattern: {template.criteria.name_pattern}</div>
                  )}
                  {template.criteria.size_min && (
                    <div>Min size: {(template.criteria.size_min / 1024 / 1024).toFixed(1)} MB</div>
                  )}
                  {template.criteria.size_max && (
                    <div>Max size: {(template.criteria.size_max / 1024 / 1024).toFixed(1)} MB</div>
                  )}
                  {template.criteria.tags && template.criteria.tags.length > 0 && (
                    <div>Required tags: {template.criteria.tags.length} tags</div>
                  )}
                </div>
              </div>
              
              {/* Actions */}
              <div>
                <h6 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">ACTIONS</h6>
                <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  {template.actions.add_tags && template.actions.add_tags.length > 0 && (
                    <div>Add tags: {template.actions.add_tags.length} tags</div>
                  )}
                  {template.actions.set_category && (
                    <div>Set category: {template.actions.set_category}</div>
                  )}
                  {(!template.actions.add_tags || template.actions.add_tags.length === 0) && 
                   !template.actions.set_category && (
                    <div className="text-gray-400">No actions defined</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Sorting Template</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleCreateTemplate} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-3 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="e.g., Auto-tag Receipts"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-3 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="What this template does..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Folder Structure (Path-based) */}
              <div className="border-t pt-6">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Folder Structure</h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={formData.actions.folder_structure}
                    onChange={(e) => {
                      setFormData({ ...formData, actions: { ...formData.actions, folder_structure: e.target.value } });
                      updatePathStyleHints(e.target.value);
                    }}
                    className="w-full p-3 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="e.g., Documents/<Year>/<Category>/ or Photos/<Person>/<Age>/"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Use &lt;Variable&gt; for dynamic values.</p>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
                    <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">Structure insights</p>
                    <ul className="text-xs text-gray-600 dark:text-gray-300 list-disc list-inside">
                      <li>Nesting depth: <span className="font-medium">{pathStyleHints.nestingDepth}</span></li>
                      <li>Uses year segment: <span className="font-medium">{pathStyleHints.usesYearSegment ? 'Yes' : 'No'}</span></li>
                      <li>Uses category segment: <span className="font-medium">{pathStyleHints.usesCategorySegment ? 'Yes' : 'No'}</span></li>
                      <li>Variables detected: <span className="font-medium">{pathStyleHints.variablesCount}</span></li>
                    </ul>
                  </div>
                  {pathSuggestions.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">Suggested structures</p>
                      <div className="flex flex-wrap gap-2">
                        {pathSuggestions.map((sug) => (
                          <button
                            key={sug}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, actions: { ...formData.actions, folder_structure: sug } });
                              updatePathStyleHints(sug);
                              sendSuggestionFeedback('template_suggestion', 'accept', { suggestion: sug }).catch(() => {});
                            }}
                            className="px-2.5 py-1 text-xs rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 hover:bg-orange-200 dark:hover:bg-orange-800"
                          >
                            {sug}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              

              {/* Actions Section */}
              <div className="border-t pt-6">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Actions to Perform</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Add Tags
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <button
                          key={tag.tag_id}
                          type="button"
                          onClick={() => toggleTag(tag.tag_id, true)}
                          className={`inline-flex items-center px-3 py-1 text-sm rounded transition duration-200 ${
                            formData.actions.add_tags.includes(tag.tag_id)
                              ? 'text-white'
                              : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                          }`}
                          style={{
                            backgroundColor: formData.actions.add_tags.includes(tag.tag_id) ? tag.color : undefined
                          }}
                        >
                          <i className={`fas fa-${tag.icon} mr-1`}></i>
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  
                </div>
              </div>

              {/* Settings */}
              <div className="border-t pt-6">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Settings</h4>
                
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Template is active</span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.auto_apply}
                      onChange={(e) => setFormData({ ...formData, auto_apply: e.target.checked })}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Auto-apply to new uploads</span>
                  </label>
                </div>
              </div>
              
              <div className="flex space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 rounded transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
                >
                  Create Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Template Modal (similar to create, but with update logic) */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Edit Template</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleEditTemplate} className="space-y-6">
              {/* Same form structure as create modal */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-3 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-3 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={3}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Template is active</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.auto_apply}
                    onChange={(e) => setFormData({ ...formData, auto_apply: e.target.checked })}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Auto-apply to new uploads</span>
                </label>
              </div>
              
              <div className="flex space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 rounded transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-medium leading-normal bg-red-400 hover:bg-red-300 text-white rounded transition duration-200"
                >
                  Update Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManager;










